import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';

/**
 * What an AI generation costs, and what is left.
 *
 * Exists so the cost can be shown AT the generate button. Until now the balance
 * lived only on the Settings page and no generate button anywhere said a click
 * costs credits — so the person spending could not see it and the person paying
 * found out later. That is a worse problem with a contractor on the tools.
 *
 * Reads `/admin/ai/budget`, which is deliberately separate from `/admin/ai/credits`:
 * that one moves money (top-ups, auto-top-up, the grants ledger) and stays
 * admin-only.
 */

export interface AiBudget {
  balance: {
    remaining: number | null;
    included: number | null;
    used: number | null;
    unmetered: boolean;
  } | null;
  costs: { image: number; image_premium: number; text: number } | null;
}

export function useAiBudget(enabled = true) {
  return useQuery({
    queryKey: ['ai-budget'],
    enabled,
    // Advisory, and it changes as generations land — but re-fetching on every
    // modal open would be noise. A minute is close enough to be useful.
    staleTime: 60_000,
    retry: false,
    queryFn: () => api.get<AiBudget>('/admin/ai/budget'),
  });
}

/** One line of plain English, or null when there is nothing worth saying. */
export function describeBudget(budget: AiBudget | undefined): string | null {
  if (!budget?.costs) return null;
  const cost = `About ${budget.costs.image} credits per image`;
  if (!budget.balance || budget.balance.unmetered) return `${cost}.`;
  if (typeof budget.balance.remaining !== 'number') return `${cost}.`;
  return `${cost} · ${budget.balance.remaining.toLocaleString()} left`;
}
